/**
 * Equipment → "How it works": a plain, read-only walkthrough of the
 * booking process for staff, with an extra section for admins. No data,
 * no calls — the place to point a colleague to before their first
 * request. Kept in the app (not a PDF) so it always matches the UI.
 */
import type { JSX } from "preact";

const STEPS: Array<{ n: string; title: string; body: string[] }> = [
  {
    n: "1",
    title: "Book",
    body: [
      "Under Book, choose the pickup date and time (Mon–Fri, 09:30–16:30, no closures) and the expected return date, then pick the items and quantities you need. The list shows what is actually free for that window.",
      "You book for yourself. Add the purpose or project so the admins can judge the request quickly. You receive a confirmation email straight away.",
    ],
  },
  {
    n: "2",
    title: "Approval",
    body: [
      "The equipment admins review the request and approve it — sometimes with reduced quantities — or decline it with a reason. You get an email either way, and the request shows the outcome under My requests.",
      "Need to change your mind? Cancel it under My requests any time before the pickup so the kit is free for others.",
    ],
  },
  {
    n: "3",
    title: "Pickup",
    body: [
      "Collect the kit from General Affairs on the agreed day. The colleague handing it over registers the pickup in the app; you receive an email with the list of what you took and when it is due back.",
      "Can't come yourself? Under My requests press \"Someone else collects\" and name the colleague: they and General Affairs are emailed, the name is pre-filled at pickup and the booking stays yours.",
    ],
  },
  {
    n: "4",
    title: "While you have it",
    body: [
      "Under My requests each booking has its own buttons: Change return date (weekdays only, the items must still be free; General Affairs is told by email), Someone else collects (a colleague picks it up for you, the booking stays yours — they and General Affairs are emailed) and Edit purpose.",
      "Handing the kit to a colleague for good? Hand over to a colleague: the booking and the responsibility pass to them, but only once BOTH they and General Affairs have accepted — both are emailed at once and can answer in any order. Until then you remain responsible. Anything waiting for you — a hand-over to accept, a report to confirm, a kit to collect for someone — is listed at the top of My requests.",
    ],
  },
  {
    n: "5",
    title: "Return and confirmation",
    body: [
      "Bring everything back to General Affairs. The colleague receiving it checks the items and issues a return report in the app: what came back, anything lost or damaged, with notes.",
      "You receive an email and find the report under My requests, with a PDF preview. Read it and either Confirm it — which closes the booking — or Dispute it with a note if something is wrong; the admin then corrects and re-issues it.",
      "Once confirmed, the report PDF with both acceptances is stored on the booking and stays downloadable next to it. No paper, no signatures.",
    ],
  },
];

/** The admins' side of the same process, step by step (Manage tab). */
const ADMIN_STEPS: Array<{ n: string; title: string; body: string[] }> = [
  {
    n: "A",
    title: "Review requests",
    body: [
      "Manage → Requests lists every booking, Pending first. Review… approves (optionally with reduced quantities and a note) or rejects with a reason; the requester is emailed either way. Edit… changes who it is for, the dates, the items and the notes; Delete removes a request for good (also closed ones).",
      "Hand-overs proposed by holders appear on the booking with Approve / Reject. The transfer takes effect only when both you and the new holder have accepted.",
    ],
  },
  {
    n: "B",
    title: "Hand the kit over",
    body: [
      "Manage → Pickups & returns → Confirm pickup…: record who collected it (the holder or a colleague sent by them) and who handed it over. The holder is emailed with the list and the due date.",
    ],
  },
  {
    n: "C",
    title: "Take it back and issue the return report",
    body: [
      "Issue return report…: for each item, how many came back, how many are lost or damaged, with a short note on any damage. Issue it: the booking becomes \"Return to confirm\" and the holder is asked by email to confirm it in the app.",
      "If the holder disputes it, you receive an email: Amend report… fixes and re-issues it. If they never answer, Close without confirmation… (note required) closes the booking. On closure the PDF with both acceptances is stored on the booking and the damaged / lost units are applied to the inventory.",
    ],
  },
  {
    n: "D",
    title: "Keep the inventory right",
    body: [
      "Manage → Inventory: the roster (Total) stays as it is; each unit is Available, Damaged or Lost. Click + to set a unit aside as damaged or write it off as lost, − when it is repaired or found — it is bookable again at once, and every click is kept in the item's history. Edit… is for the roster itself (name, total, notes, active).",
      "Availability shows what is free for any date window; ↓ Export register downloads the whole register as an Excel file.",
    ],
  },
  {
    n: "E",
    title: "Archive and notifications",
    body: [
      "Manage → Archive: download every stored return report as one ZIP (files named reference_date_holder) and, once saved, remove them from storage. Inventory → Storage & retention purges history older than 24 months.",
      "Manage → Notifications shows every email sent, to whom, and lets you retry failures. Which admins receive the admin emails is set with the 🔔 in the Directory by a super admin.",
    ],
  },
];

export function EquipmentGuide({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  return (
    <div class="eq-guide">
      <div class="eq-card eq-guide-intro">
        <h3 class="eq-section-title">How equipment booking works</h3>
        <p>
          ICONS' shared equipment (phones, tripods, microphones, audio recorders, adapters…) is held
          by <b>General Affairs</b> and booked here instead of the old Excel register. Everyone can
          book for themselves; the equipment admins approve, hand over and receive the kit. Every
          step sends an email, and everything is recorded on the booking.
          {isAdmin && <> You are an admin: your part of the process is described at the bottom of this page.</>}
        </p>
      </div>
      <ol class="eq-guide-steps">
        {STEPS.map((st) => (
          <li key={st.n} class="eq-card eq-guide-step">
            <div class="eq-guide-n" aria-hidden="true">{st.n}</div>
            <div>
              <h4>{st.title}</h4>
              {st.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </li>
        ))}
      </ol>
      <div class="eq-card eq-guide-faq">
        <h4>Good to know</h4>
        <ul>
          <li><b>Statuses:</b> Pending → Approved → Collected → Return to confirm → Returned. Rejected and Cancelled end a request without movement.</li>
          <li><b>Overdue</b> means the kit is out past its expected return date — move the date under Edit if you need it longer, before it expires.</li>
          <li><b>Lost or damaged items</b> are declared in the return report. Both are taken out of what can be booked; a damaged unit comes back the moment General Affairs marks it repaired.</li>
          <li><b>Your data:</b> the register stores only what the process needs — names, dates, quantities, supervisors. History older than 24 months is purged by the admins.</li>
        </ul>
      </div>
      {isAdmin && (
        <>
          <div class="eq-card eq-guide-intro eq-guide-admin-intro">
            <h3 class="eq-section-title">For admins — your side of the process</h3>
            <p>
              Everything below happens under <b>Manage</b>. The steps mirror what the person booking
              sees above: you approve, hand over, take back and issue the report, and keep the
              inventory right.
            </p>
          </div>
          <ol class="eq-guide-steps">
            {ADMIN_STEPS.map((st) => (
              <li key={st.n} class="eq-card eq-guide-step eq-guide-step-admin">
                <div class="eq-guide-n" aria-hidden="true">{st.n}</div>
                <div>
                  <h4>{st.title}</h4>
                  {st.body.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
